/**
 * View-model for the Orders list: one flat row per guest order, with the
 * money already worked out (paid, balance) and every label resolved, so the
 * screen, its search and its summary cards all read the same fields.
 * Money stays an exact decimal string; nothing here uses floats except the
 * sort key.
 */
import type { FabricAssignment, FabricAssignmentStatus, PaymentStatus } from '../types/ui/Asoebi';
import { compareDecimal, subtractDecimal, sumByCurrency } from './decimal';
import { fabricRefName, isBundleRef, type NamedRow } from './fabricRef';

export const ORDER_STATUS_OPTIONS: ReadonlyArray<{ value: FabricAssignmentStatus; label: string }> = [
  { value: 'pending_confirmation', label: 'Awaiting confirmation' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'paid', label: 'Paid' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'collected', label: 'Collected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'returned', label: 'Returned' },
];

export const ORDER_PAYMENT_OPTIONS: ReadonlyArray<{ value: PaymentStatus; label: string }> = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partly paid' },
  { value: 'paid', label: 'Paid in full' },
];

export const ORDER_STATUS_LABELS: Record<string, string> = Object.fromEntries(ORDER_STATUS_OPTIONS.map(o => [o.value, o.label]));
export const ORDER_PAYMENT_LABELS: Record<string, string> = Object.fromEntries(ORDER_PAYMENT_OPTIONS.map(o => [o.value, o.label]));

/** Orders that no longer represent money owed to, or earned by, the project. */
const VOID_STATUSES = new Set<string>(['cancelled', 'returned']);

export interface OrderListRow {
  id: string;
  /** The record label shown and linked (recordLabel at build time). */
  ref: string;
  guest_name: string;
  guest_phone: string;
  fabric_name: string;
  /** Yards of a fabric, or packages of a bundle, as a plain number string. */
  quantity: string;
  unit: 'yard' | 'package';
  status: string;
  status_label: string;
  payment_status: string;
  payment_label: string;
  /** Money is null when the server withheld it (a role that may not see money). */
  total_price: string | null;
  paid: string | null;
  balance: string | null;
  currency: string | null;
  created_at: string;
  archived: boolean;
  read_only: boolean;
}

export interface OrderRowContext {
  fabrics: readonly NamedRow[];
  bundles: readonly NamedRow[];
  /** recordLabel('fabricAssignment', id), injected so this module stays pure. */
  refLabel: (id: string) => string;
  /** The project currency, used for a row that carries none of its own. */
  fallbackCurrency: string;
}

/** 2 -> "2", "2.50" -> "2.5": a quantity without trailing zeros. */
export function plainQuantity(value: string | number | null | undefined): string {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '0';
}

export function toOrderListRow(a: FabricAssignment, ctx: OrderRowContext): OrderListRow {
  const hasMoney = a.total_price != null;
  const isVoid = VOID_STATUSES.has(a.status);
  const paid = hasMoney ? (a.payment_amount ?? '0') : null;
  let balance: string | null = null;
  if (hasMoney && paid !== null) {
    const owing = compareDecimal(a.total_price, paid) === 1 ? subtractDecimal(a.total_price, paid) : '0';
    balance = isVoid ? '0' : owing;
  }
  return {
    id: String(a.id),
    ref: ctx.refLabel(String(a.id)),
    guest_name: a.guest_name?.trim() || 'Unnamed guest',
    guest_phone: a.guest_phone ?? '',
    fabric_name: fabricRefName(a, ctx.fabrics, ctx.bundles, 'Unknown fabric'),
    quantity: plainQuantity(a.yards),
    unit: isBundleRef(a, ctx.bundles) ? 'package' : 'yard',
    status: a.status,
    status_label: ORDER_STATUS_LABELS[a.status] ?? a.status,
    payment_status: a.payment_status,
    payment_label: ORDER_PAYMENT_LABELS[a.payment_status] ?? a.payment_status,
    total_price: hasMoney ? a.total_price : null,
    paid,
    balance,
    currency: a.currency || ctx.fallbackCurrency || null,
    created_at: a.created_at,
    archived: Boolean(a.archived),
    read_only: Boolean(a.read_only),
  };
}

export interface OrderListFilters {
  status: string;
  payment: string;
  /** The global header search. */
  search: string;
  /** False for a role that may not see money: the payment state (and so the Payment filter and its words in search) is money-derived and is ignored. */
  canViewFinancials: boolean;
}

/** Guest, phone, record label, id, fabric or bundle name, and status words. */
export function orderMatchesSearch(row: OrderListRow, search: string, withPayment = true): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  const fields = [row.guest_name, row.guest_phone, row.ref, row.id, row.fabric_name, row.status_label];
  return (withPayment ? [...fields, row.payment_label] : fields).some(v => v.toLowerCase().includes(term));
}

export function filterOrderRows(rows: readonly OrderListRow[], f: OrderListFilters): OrderListRow[] {
  return rows.filter(r => (!f.status || r.status === f.status)
    && (!f.payment || !f.canViewFinancials || r.payment_status === f.payment)
    && orderMatchesSearch(r, f.search, f.canViewFinancials));
}

export interface OrderListSummary {
  count: number;
  /** Per-currency exact totals ({ GBP: "120.00" }); cancelled and returned orders are left out. */
  value: Record<string, string>;
  collected: Record<string, string>;
  outstanding: Record<string, string>;
}

export function summarizeOrders(rows: readonly OrderListRow[]): OrderListSummary {
  const live = rows.filter(r => !VOID_STATUSES.has(r.status));
  const by = (pick: (r: OrderListRow) => string | null) =>
    sumByCurrency([...live], r => ({ amount: pick(r), currency: r.currency }));
  return {
    count: rows.length,
    value: by(r => r.total_price),
    collected: by(r => r.paid),
    outstanding: by(r => r.balance),
  };
}
